import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Eye, FolderEdit, Image, Save } from 'lucide-react';
import AdminShell from '../../admin/components/layout/AdminShell';
import AdminPageHeader from '../../admin/components/shared/AdminPageHeader';
import AdminPanel from '../../admin/components/shared/AdminPanel';
import AdminStatCard from '../../admin/components/shared/AdminStatCard';
import AdminStatusBadge from '../../admin/components/shared/AdminStatusBadge';
import SecondaryActionButton from '../../admin/components/buttons/SecondaryActionButton';
import PublishActionButton from '../../admin/components/buttons/PublishActionButton';
import PrimaryActionButton from '../../admin/components/buttons/PrimaryActionButton';
import EmptyStatePanel from '../../admin/components/shared/EmptyStatePanel';
import MenuSectionPanel from '../../components/public/MenuSectionPanel';
import SourceMenuGallery from '../../components/public/SourceMenuGallery';
import { useAdminContext } from '../../context/AdminContext';
import { menuSourcePages } from '../../data/menuSourcePages';
import { sortCategories } from '../../utils/workspaceHelpers';
import { auditWorkspace } from '../../utils/workspaceValidation';

const AdminDashboardPage: React.FC = () => {
  const { draftWorkspace, draftStatus, draftError, saveDraft, saving, publishDraft, publishing, draftMeta, dirty } =
    useAdminContext();

  const previewCategories = useMemo(
    () => (draftWorkspace ? sortCategories(draftWorkspace.categories).slice(0, 2) : []),
    [draftWorkspace],
  );
  const hotelName = draftWorkspace?.settings.name?.trim() || 'the restaurant';
  const workspaceAudit = useMemo(
    () => (draftWorkspace ? auditWorkspace(draftWorkspace) : null),
    [draftWorkspace],
  );
  const draftSettings = draftWorkspace?.settings;
  const checklist = workspaceAudit
    ? [
        workspaceAudit.settingsMissing.length > 0
          ? `${workspaceAudit.settingsMissing.length} restaurant detail${workspaceAudit.settingsMissing.length === 1 ? '' : 's'} still missing`
          : null,
        !draftSettings?.mapsUrl.trim() ? 'Map link still missing' : null,
        !draftSettings?.logoImage.trim() ? 'Logo image still missing' : null,
        !draftSettings?.faviconImage.trim() ? 'Tab icon still missing' : null,
        workspaceAudit.itemsWithoutImage > 0
          ? `${workspaceAudit.itemsWithoutImage} item photo${workspaceAudit.itemsWithoutImage === 1 ? '' : 's'} still missing`
          : null,
        workspaceAudit.itemsWithoutDescription > 0
          ? `${workspaceAudit.itemsWithoutDescription} item description${workspaceAudit.itemsWithoutDescription === 1 ? '' : 's'} still missing`
          : null,
        workspaceAudit.categoriesWithoutVisibleItems > 0
          ? `${workspaceAudit.categoriesWithoutVisibleItems} categor${workspaceAudit.categoriesWithoutVisibleItems === 1 ? 'y has' : 'ies have'} no visible items`
          : null,
        workspaceAudit.duplicateCategorySlugs.length > 0 || workspaceAudit.duplicateItemSlugs.length > 0
          ? 'Some slugs are duplicated and must be fixed before publishing'
          : null,
      ].filter((value): value is string => Boolean(value))
    : [];

  return (
    <AdminShell>
      <AdminPageHeader
        eyebrow="Admin Dashboard"
        title="Draft status and publish control"
        description={`Review the shared ${hotelName} draft, save changes, and push the live menu only when it’s ready.`}
        actions={
          <>
            <SecondaryActionButton onClick={() => void saveDraft()} disabled={saving || !dirty}>
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Draft'}
            </SecondaryActionButton>
            <PublishActionButton onClick={() => void publishDraft()} disabled={publishing || draftStatus !== 'ready'}>
              {publishing ? 'Publishing...' : 'Publish Live'}
            </PublishActionButton>
          </>
        }
      />

      {draftError && (
        <AdminPanel className="p-4 text-sm text-red-100">
          <div className="rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3">{draftError}</div>
        </AdminPanel>
      )}

      {!draftWorkspace || draftStatus === 'loading' ? (
        <AdminPanel className="p-8 text-center text-white/60">Loading the shared draft workspace...</AdminPanel>
      ) : (
        <>
          <div className="grid gap-4 xl:grid-cols-6">
            <AdminStatCard label="Categories" value={`${draftWorkspace.categories.length}`} hint="Visible and hidden menu groups in the draft" />
            <AdminStatCard label="Items" value={`${draftWorkspace.items.length}`} hint="Editable menu items, prices, and availability" />
            <AdminStatCard label="Media" value={`${draftWorkspace.mediaAssets.length}`} hint="Uploaded and reusable images in storage" />
            <AdminStatCard
              label="Missing Images"
              value={`${workspaceAudit?.itemsWithoutImage ?? 0}`}
              hint="Items still using category or hotel fallback visuals"
            />
            <AdminStatCard
              label="Missing Details"
              value={`${workspaceAudit?.itemsWithoutDescription ?? 0}`}
              hint="Items without short visible menu descriptions"
            />
            <AdminStatCard
              label="Status"
              value={draftMeta.hasUnpublishedChanges || dirty ? 'Draft' : 'Live'}
              hint={draftMeta.publishedAt ? `Last publish ${new Date(draftMeta.publishedAt).toLocaleDateString()}` : 'Not published yet'}
            />
          </div>

          <AdminPanel className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.35em] text-amber-100/45">Manager Checklist</div>
                <h2 className="mt-3 text-2xl font-semibold text-white">What still needs attention</h2>
              </div>
              <AdminStatusBadge tone={checklist.length === 0 ? 'live' : 'draft'}>
                {checklist.length === 0 ? 'Ready to publish' : `${checklist.length} thing${checklist.length === 1 ? '' : 's'} to review`}
              </AdminStatusBadge>
            </div>

            {checklist.length === 0 ? (
              <div className="mt-6 rounded-[24px] border border-emerald-300/20 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-100">
                The draft looks complete: business details are filled, images are attached where expected, and there are no duplicate slugs.
              </div>
            ) : (
              <div className="mt-6 grid gap-3 md:grid-cols-2">
                {checklist.map((item) => (
                  <div key={item} className="rounded-[24px] border border-white/10 bg-white/5 px-4 py-4 text-sm text-white/72">
                    {item}
                  </div>
                ))}
              </div>
            )}
          </AdminPanel>

          <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
            <AdminPanel className="p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.35em] text-amber-100/45">Publish State</div>
                  <h2 className="mt-3 text-2xl font-semibold text-white">Draft versus live menu</h2>
                </div>
                <AdminStatusBadge tone={draftMeta.hasUnpublishedChanges || dirty ? 'draft' : 'live'}>
                  {draftMeta.hasUnpublishedChanges || dirty ? 'Unpublished changes' : 'Live is up to date'}
                </AdminStatusBadge>
              </div>

              <div className="mt-6 space-y-4 text-sm text-white/60">
                <div className="rounded-[24px] border border-white/10 bg-white/5 p-4">
                  <div className="text-[11px] uppercase tracking-[0.28em] text-amber-100/45">Draft Updated</div>
                  <div className="mt-2 text-white">{draftMeta.draftUpdatedAt ? new Date(draftMeta.draftUpdatedAt).toLocaleString() : 'Not saved yet'}</div>
                </div>
                <div className="rounded-[24px] border border-white/10 bg-white/5 p-4">
                  <div className="text-[11px] uppercase tracking-[0.28em] text-amber-100/45">Published Live</div>
                  <div className="mt-2 text-white">{draftMeta.publishedAt ? new Date(draftMeta.publishedAt).toLocaleString() : 'Nothing published yet'}</div>
                </div>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <Link to="/admin/menu/categories">
                  <PrimaryActionButton fullWidth>
                    <FolderEdit className="h-4 w-4" />
                    Edit Categories
                  </PrimaryActionButton>
                </Link>
                <Link to="/admin/media">
                  <SecondaryActionButton fullWidth>
                    <Image className="h-4 w-4" />
                    Open Media
                  </SecondaryActionButton>
                </Link>
              </div>
            </AdminPanel>

            <AdminPanel className="p-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.35em] text-amber-100/45">Quick Access</div>
                  <h2 className="mt-3 text-2xl font-semibold text-white">Fast paths for daily updates</h2>
                </div>
                <button
                  type="button"
                  onClick={() => document.getElementById('admin-preview')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-amber-300"
                >
                  <Eye className="h-4 w-4" />
                  Preview Draft
                </button>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {[
                  {
                    to: '/admin/menu/categories',
                    title: 'Categories',
                    description: 'Cover images, order, visibility, and short descriptions.',
                  },
                  {
                    to: '/admin/menu/items',
                    title: 'Items',
                    description: 'Item names, prices, availability, descriptions, and food photos.',
                  },
                  {
                    to: '/admin/settings',
                    title: 'Settings',
                    description: 'Business details, map link, logo, favicon, colors, and the main cover image.',
                  },
                  {
                    to: '/admin/media',
                    title: 'Media Library',
                    description: 'Upload reusable food, cover, logo, and branding images from the browser.',
                  },
                ].map((card) => (
                  <Link key={card.to} to={card.to} className="rounded-[24px] border border-white/10 bg-white/5 p-5 transition hover:-translate-y-0.5 hover:bg-white/8">
                    <div className="text-lg font-semibold text-white">{card.title}</div>
                    <div className="mt-2 text-sm leading-7 text-white/55">{card.description}</div>
                  </Link>
                ))}
              </div>
            </AdminPanel>
          </div>

          <div id="admin-preview" className="space-y-6">
            <AdminPageHeader
              eyebrow="Draft Preview"
              title="Preview the current draft with public menu components"
              description="This preview uses the same menu presentation pattern as the live site, but it renders the unpublished draft."
            />

            {previewCategories.length === 0 ? (
              <EmptyStatePanel title="No categories yet" description="Create your first category to start previewing the draft menu." />
            ) : (
              previewCategories.map((category) => (
                <MenuSectionPanel
                  key={category.id}
                  category={category}
                  items={draftWorkspace.items
                    .filter((item) => item.categoryId === category.id)
                    .sort((left, right) => left.sortOrder - right.sortOrder)
                    .slice(0, 4)}
                />
              ))
            )}
          </div>

          <div className="space-y-6">
            <AdminPageHeader
              eyebrow="Source Photos"
              title="Original menu booklet pages from the image folder"
              description="Use these source pages to compare the extracted food and drink names with the photographed menu."
            />
            <SourceMenuGallery pages={menuSourcePages} />
          </div>
        </>
      )}
    </AdminShell>
  );
};

export default AdminDashboardPage;
